import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, get } from "firebase/database";
import { firestoreDB, db } from "../../config/firebase_config.js";
import { LoadingSpinner } from "../../components/mini-components/mini-components";
import {
  calculateOriginalPrice,
  successToast,
  errorToast,
} from "../../functions/mini_func";
import BillTile from "../checkout-page/components/bill-details/BillTile.jsx";
import FlashpeImg from "../../assets/images/splash_image.jpg";
import { MdVerified } from "react-icons/md";
import "../order-page/orderPage.css";

// Same gateway-decision node and Cloud Functions used by CheckOutPage.
const PAYMENT_GATEWAY_REF = "/PaymentGateway/8886666847/paymentGateway";
const PAYU_HASH_URL = "https://payuhashfunction-jdoxig6lva-uc.a.run.app";
const INIT_PAYTM_URL =
  "https://us-central1-thediningapp-a976c.cloudfunctions.net/initPaytm";
const PAYTM_MID = "bDPTCj55212214623831";

const OrderTile = ({ name, price, quantity, isLast, discount }) => (
  <>
    <div className="order-tile !text-xs">
      <div className="order-name-info !text-xs">{name}</div>
      <span>
        {"\u{20B9}\t"} {calculateOriginalPrice(discount, price)}
      </span>
      <h5 className="!text-xs">&times; {quantity}</h5>
    </div>
    {!isLast && <div className="dotted-border"></div>}
  </>
);

const TableOrderBookingPage = () => {
  const { id } = useParams();

  const [bill, setBill] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!id) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      try {
        const snap = await getDoc(doc(firestoreDB, "tableOrderBills", id));
        if (!snap.exists()) {
          setNotFound(true);
          return;
        }
        const data = { id: snap.id, ...snap.data() };
        setBill(data);

        // Best-effort restaurant info (phone / address) to match the order page.
        if (data.storeId) {
          try {
            const restSnap = await get(
              ref(db, `RestaurantMenu/${data.storeId}`),
            );
            if (restSnap.exists()) setRestaurant(restSnap.val());
          } catch (e) {
            console.log("restaurant fetch failed", e);
          }
        }
      } catch (e) {
        console.error("Failed to load bill:", e);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const total = bill ? Math.round(bill.totalAmount || 0) : 0;

  const markBillPaid = async () => {
    if (!bill) return;
    try {
      await updateDoc(doc(firestoreDB, "tableOrderBills", bill.id), {
        paymentStatus: "paid",
        paidAt: new Date().toISOString(),
      });
      setBill((prev) => ({ ...prev, paymentStatus: "paid" }));
      successToast("Payment Successful", "Your bill has been paid. Thank you!");
    } catch (e) {
      console.log("Failed to update bill status", e);
    }
  };

  // ---- PayU (same as CheckOutPage.getPayUrl / PayBill.handleFinalPay) ----
  const payViaPayU = async () => {
    const phone = (bill.customerPhone || "").toString().replace(/\D/g, "");
    const txnId = Date.now().toString();
    const productInfo = `${bill.restaurantName || "Restaurant"} - Table ${
      bill.tableNo || ""
    } bill`;

    // Remembered so /payuPayments can mark this bill paid on success.
    localStorage.setItem("pendingTableBillId", bill.id);

    const response = await fetch(PAYU_HASH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        amount: total.toString(),
        productInfo,
        firstName: "Guest",
        email: "guest@thediningapp.com",
        phone,
        txnId,
        paymentType: "BILL_PAYMENT",
        storeId: bill.storeId || "",
        billId: bill.id,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      // Cloud Function returns a self-submitting PayU form.
      document.open();
      document.write(data.html);
      document.close();
    } else {
      errorToast(
        "Transaction failed",
        "Unable to start payment. Please try later.",
      );
      setIsProcessing(false);
    }
  };

  // ---- Paytm (same flow as CheckOutPage.displayPaytm / initializePaytmCheckout) ----
  const launchPaytmCheckout = (orderId, token, amount) =>
    new Promise((resolve, reject) => {
      const onScriptLoad = () => {
        const config = {
          root: "",
          flow: "DEFAULT",
          redirect: false,
          data: {
            orderId: orderId.split("#")[1],
            token,
            tokenType: "TXN_TOKEN",
            amount,
            userDetail: {
              mobileNumber: bill.customerPhone,
              name: "Guest",
            },
            merchant: {
              mid: PAYTM_MID,
              redirect: false,
              hidePaytmBranding: true,
            },
          },
          handler: {
            transactionStatus: (paymentStatus) => {
              if (paymentStatus && paymentStatus.STATUS === "TXN_SUCCESS") {
                markBillPaid();
              }
            },
            notifyMerchant: (eventName) => {
              if (eventName === "APP_CLOSED" || eventName === "CLOSE") {
                setIsProcessing(false);
              }
            },
          },
        };

        if (window.Paytm && window.Paytm.CheckoutJS) {
          window.Paytm.CheckoutJS.onLoad(() => {
            window.Paytm.CheckoutJS.init(config)
              .then(() => {
                window.Paytm.CheckoutJS.invoke();
                resolve();
              })
              .catch(reject);
          });
        }
      };

      const script = document.createElement("script");
      script.src = `https://securegw.paytm.in/merchantpgpui/checkoutjs/merchants/${PAYTM_MID}.js`;
      script.async = true;
      script.onload = onScriptLoad;
      document.body.appendChild(script);
    });

  const payViaPaytm = async () => {
    const phone = (bill.customerPhone || "0000000000").toString();
    const orderId = `#G${phone.slice(-4)}${Date.now()}`;
    const amount = total.toString();

    const response = await fetch(INIT_PAYTM_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        amount,
        appType: "WEB",
        orderID: orderId,
        userID: phone,
      }),
    });
    const data = await response.json();
    await launchPaytmCheckout(orderId, data.body.txnToken, amount);
    setIsProcessing(false);
  };

  // ---- Gateway selection (same as CheckOutPage.paymentButtonFunc) ----
  const handlePay = async () => {
    if (!bill || total <= 0) {
      errorToast("Invalid amount", "This bill has no payable amount.");
      return;
    }
    setIsProcessing(true);
    try {
      const snap = await get(ref(db, PAYMENT_GATEWAY_REF));
      const gateway = snap.val();
      if (gateway === "Paytm") {
        await payViaPaytm();
      } else {
        // "Payu" and default
        await payViaPayU();
      }
    } catch (e) {
      console.error(e);
      errorToast("Something went wrong", "Please try again.");
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="order-loading">
        <LoadingSpinner />
        <p>
          Please wait ... <br /> while we fetch your bill
        </p>
      </div>
    );
  }

  if (notFound || !bill) {
    return (
      <div className="order-loading">
        <p style={{ color: "#ff6b6b" }}>
          This billing link is invalid or has expired.
        </p>
      </div>
    );
  }

  const isPaid = bill.paymentStatus === "paid";

  return (
    <div
      className="order-page-container gradient-background"
      style={{ paddingBottom: "6rem" }}
    >
      <div className="hide-text"></div>

      <div className="order-main-section">
        <div className="order-name">
          <h3>Your Bill</h3>
          <img src={FlashpeImg} alt="" width={"60px"} height={"60px"} />
        </div>
        <div className="order-title">
          <div>
            <p>Table : {bill.tableNo || "-"}</p>
            {isPaid && (
              <p className="order-time" style={{ color: "#56fb56" }}>
                Paid
              </p>
            )}
          </div>
          {isPaid && (
            <div className="order-verify">
              <MdVerified />
            </div>
          )}
        </div>
      </div>

      <div className="order-rest-section order-item-container">
        <h4 className="order-title-name">Restaurant Information</h4>
        <div className="rest-name">{bill.restaurantName || "Restaurant"}</div>
        {restaurant?.restaurantOwnerNo && (
          <p>
            Phone Number : <span>{restaurant.restaurantOwnerNo}</span>
          </p>
        )}
        {(restaurant?.address || restaurant?.addressOne) && (
          <p>
            Address : <span>{restaurant.address || restaurant.addressOne}</span>
          </p>
        )}
      </div>

      <div className="order-user-section order-item-container">
        <h4 className="order-title-name">Customer Information</h4>
        <p>
          Phone Number : <span>{bill.customerPhone || "-"}</span>
        </p>
      </div>

      <div className="order-items-section order-item-container">
        <h4 className="order-title-name">Order Items</h4>
        {(bill.items || []).map((item, index) => (
          <OrderTile
            key={index}
            name={item.name}
            quantity={item.qty}
            isLast={(bill.items || []).length - 1 === index}
            price={item.price}
            discount={item.discount}
          />
        ))}
      </div>

      <div className="order-bill-section order-item-container">
        <h4 className="order-title-name">Bill Details</h4>
        <BillTile name={"Sub total"} amount={Math.round(bill.foodTotal || 0)} />
        <BillTile name={"GST (5%)"} amount={Math.round(bill.gst || 0)} />
        <div className="dotted-border"></div>
        <BillTile name={"Payable Amount"} amount={total} />
      </div>

      {!isPaid && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "12px 16px",
            background:
              "linear-gradient(to top, rgba(21,16,16,0.98), rgba(21,16,16,0.6))",
          }}
        >
          <button
            onClick={handlePay}
            disabled={isProcessing}
            style={{
              width: "100%",
              padding: "0.9rem",
              border: "none",
              borderRadius: "10px",
              fontWeight: "bold",
              fontSize: "1rem",
              color: "black",
              cursor: isProcessing ? "not-allowed" : "pointer",
              background:
                "linear-gradient(135deg, rgb(248, 191, 2), rgb(251, 194, 80), rgb(252, 172, 23))",
              boxShadow: "8px 8px 10px rgba(229, 203, 9, 0.2)",
            }}
          >
            {isProcessing ? "Processing..." : `Proceed to Pay ₹${total}`}
          </button>
        </div>
      )}
    </div>
  );
};

export default TableOrderBookingPage;
